import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/** 从 region 字符串解析国家/地区简称，如 "US-East" -> "US" */
function regionToCountryCode(region: string): string {
  const part = region.split(/[-_]/)[0]?.toUpperCase() ?? region;
  if (part.length <= 3) return part;
  const map: Record<string, string> = {
    US: "US",
    CN: "CN",
    EU: "EU",
    SG: "SG",
    JP: "JP",
  };
  return map[part] ?? part.slice(0, 2);
}

/**
 * 格式化 region 展示。
 * 支持两种来源：1) 已转换的 "国内"/"国外"；2) 配置原始值 "0"/"1"；其它取国家/地区简称（如 US-East -> US）。
 */
export function formatRegionLabel(region: string): string {
  if (region === "国内" || region === "国外") return region;
  if (region === "0") return "国内";
  if (region === "1") return "国外";
  return regionToCountryCode(region);
}

/** 将数字格式化为英文数字展示方式，每三位加逗号（如 32,768） */
export function formatTokenCount(n: number): string {
  return Math.round(n).toLocaleString("en-US")
}

/** 首字延迟/吞吐量/可用性：值为 0 时返回 null，表示无数据，由调用方展示「暂无数据」 */
export function formatLatencyOrNull(s: number): string | null {
  if (s == null || Number.isNaN(s) || s === 0) return null;
  return `${s.toFixed(2)}s`;
}
/** 首字延迟（毫秒）：值为 0 时返回 null */
export function formatLatencyMsOrNull(s: number): string | null {
  if (s == null || Number.isNaN(s) || s === 0) return null;
  return `${Math.round(s * 1000)}ms`;
}
export function formatThroughputOrNull(tps: number): string | null {
  if (tps == null || Number.isNaN(tps) || tps === 0) return null;
  return Number.isInteger(tps) ? `${tps}tps` : `${tps.toFixed(2)}tps`;
}
export function formatUptimeOrNull(pct: number): string | null {
  if (pct == null || Number.isNaN(pct) || pct === 0) return null;
  return `${pct.toFixed(2)}%`;
}

/** 销售倍率（sale_factor）：0.8 表示官方价 8 折。缺失 / 非法值按 1（官方原价）处理 */
export function getSaleFactorValue(saleFactor?: number | null): number {
  if (saleFactor == null) return 1;
  const value = Number(saleFactor);
  if (!Number.isFinite(value) || value <= 0) return 1;
  return value;
}

/** 是否存在折扣：销售倍率存在且小于 1 */
export function hasSaleDiscount(saleFactor?: number | null): boolean {
  const value = getSaleFactorValue(saleFactor);
  return saleFactor != null && value < 1;
}

/**
 * 折扣展示参数：0.8 -> { zhe: "8", percent: 20 }
 * 中文用「官方 8 折」，英文用「20% off」
 */
export function getDiscountDisplay(
  saleFactor?: number | null,
): { zhe: string; percent: number } | null {
  if (!hasSaleDiscount(saleFactor)) return null;
  const value = getSaleFactorValue(saleFactor);
  const zhe = Number((value * 10).toFixed(1));
  return { zhe: String(zhe), percent: Math.round((1 - value) * 100) };
}

/** Get base path from env */
export function getBasePath(): string {
  return process.env.NEXT_PUBLIC_BASE_PATH || "";
}

/**
 * 校验登录后跳转地址，防止开放重定向（Open Redirect）。
 * 仅允许站内相对路径（以单个 "/" 开头），拒绝：
 * - 绝对 URL（http:、https:、javascript: 等任意 scheme）
 * - 协议相对地址（//evil.com）
 * - 反斜杠绕过（/\evil.com，部分浏览器会当作 //evil.com 解析）
 * 非法或缺省时回退到 fallback（默认 "/"）。
 */
export function sanitizeRedirectPath(
  target: string | null | undefined,
  fallback = "/",
): string {
  if (!target) return fallback;
  // 必须以单个 "/" 开头的相对路径
  if (target[0] !== "/") return fallback;
  // 拒绝 "//" 或 "/\\" 开头（协议相对 / 反斜杠绕过）
  if (target[1] === "/" || target[1] === "\\") return fallback;
  return target;
}

export function withLocalePrefix(
  target: string,
  locale: "en" | "zh",
  basePath = getBasePath(),
): string {
  const normalizedBasePath =
    basePath && basePath !== "/" ? basePath.replace(/\/$/, "") : "";
  const hasBasePath =
    normalizedBasePath &&
    (target === normalizedBasePath ||
      target.startsWith(`${normalizedBasePath}/`));
  const path = hasBasePath
    ? target.slice(normalizedBasePath.length) || "/"
    : target;

  if (/^\/(en|zh)(\/|$)/.test(path)) {
    return target;
  }

  const localizedPath = path === "/" ? `/${locale}` : `/${locale}${path}`;
  return `${hasBasePath ? normalizedBasePath : ""}${localizedPath}`;
}
